import React, { useState, useEffect, useMemo, useRef } from 'react';
import { cn } from '@/lib/utils';
import useNotes from '@/hooks/useNotes';
import { Note } from '@/types';
import { api } from '@/services/api';
import NotesNav from '@/components/note/NotesNav';
import FloatingNav from '@/components/common/FloatingNav';
import RecentNotesPanel from '@/components/note/RecentNotesPanel';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { User, Bot, Volume2, Sparkles, SpellCheck } from 'lucide-react';
import AISwitch from '@/components/note/AISwitch';
import { RichTextEditor } from '@/components/note/RichTextEditor';
import debounce from 'lodash/debounce';
import { useEditor } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { Image } from "@tiptap/extension-image";
import { TaskItem } from "@tiptap/extension-task-item";
import { TaskList } from "@tiptap/extension-task-list";
import { TextAlign } from "@tiptap/extension-text-align";
import { Typography } from "@tiptap/extension-typography";
import { Highlight } from "@tiptap/extension-highlight";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { Underline } from "@tiptap/extension-underline";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const NotesPage = () => {
  const [notes, setNotes] = useNotes();
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [showRecentNotes, setShowRecentNotes] = useState(false);
  const [isAIEnabled, setIsAIEnabled] = useState(false);
  const [titleEdited, setTitleEdited] = useState(false);
  const [grammarModalOpen, setGrammarModalOpen] = useState(false);
  const [grammarLoading, setGrammarLoading] = useState(false);
  const [grammarOriginalHtml, setGrammarOriginalHtml] = useState<string | null>(null);
  const [grammarCorrectedHtml, setGrammarCorrectedHtml] = useState<string | null>(null);
  const editorRef = useRef<any>(null);

  // Debug log for notes and selectedNote
  console.log("Notes array:", notes, "Selected note:", selectedNote);

  useEffect(() => {
    if (notes.length > 0 && !selectedNote) {
      const mostRecent = [...notes].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())[0];
      setSelectedNote(mostRecent);
    }
  }, [notes, selectedNote]);

  // Helper to detect if note is a draft
  const isDraft = (note: any): note is { id: string } => note && !note.note_id && note.id && typeof note.id === 'string' && note.id.startsWith('draft-');

  // Debounced handlers
  const debouncedHandleUpdateNote = useMemo(() => debounce(handleUpdateNote, 600), [selectedNote, notes]);
  const debouncedHandleFirstEdit = useMemo(() => debounce(handleFirstEdit, 600), [selectedNote, notes]);

  // On first edit, create note in backend and replace draft
  async function handleFirstEdit(field: 'title' | 'content', value: string) {
    if (!selectedNote) return;
    if (isDraft(selectedNote)) {
      // Call create API
      const created = await api.createOrUpdateNote({
        title: field === 'title' ? value : selectedNote.title || 'Untitled Note',
        content: field === 'content' ? value : selectedNote.content || '',
        sequence: selectedNote.sequence ?? 1,
        status: selectedNote.status ?? 'ACTIVE',
      });
      setNotes({ type: 'replaceDraft', note: created, draftId: (selectedNote as any).id });
      setSelectedNote(created);
    } else {
      // Already persisted, just update
      handleUpdateNote({ ...selectedNote, [field]: value });
    }
  }

  function handleNewNote() {
    const draftId = `draft-${Date.now()}`;
    const newNote: any = {
      id: draftId, // temp id for draft
      title: '',
      content: '',
      sequence: 1,
      status: 'ACTIVE',
    };
    setNotes({ type: 'create', note: newNote });
    setSelectedNote(newNote);
    setTitleEdited(false);
  }

  function handleUpdateNote(updatedNote: Note) {
    setSelectedNote(updatedNote);
    setNotes({ type: 'update', note: updatedNote });
  }

  const handleDeleteNote = (note_id: string) => {
    setNotes({ type: 'delete', note: { note_id } as Note });
    if (selectedNote && selectedNote.note_id === note_id) {
      setSelectedNote(null);
    }
  };

  // Debug log for selectedNote and its content
  if (selectedNote) {
    console.log("Rendering RichTextEditor with content:", selectedNote.content);
  }

  // Utility to extract title from Tiptap JSON content
  function extractTitleFromTiptapJson(json: any): string {
    if (!json || !json.content) return "Untitled Note";
    // Find first heading
    const heading = json.content.find((node: any) => node.type === "heading" && node.content && node.content.length > 0);
    if (heading) {
      return heading.content.map((c: any) => c.text).join(" ").trim();
    }
    // Fallback: first non-empty paragraph
    const para = json.content.find((node: any) => node.type === "paragraph" && node.content && node.content.length > 0);
    if (para) {
      return para.content.map((c: any) => c.text).join(" ").trim();
    }
    return "Untitled Note";
  }

  // Remove debouncedSetSelectedNoteTitle, use immediate update instead
  const setSelectedNoteTitle = (title: string) => {
    setSelectedNote((prev) => prev ? { ...prev, title } : prev);
  };

  // Pass editor instance from RichTextEditor
  const handleEditorReady = (editor: any) => {
    editorRef.current = editor;
  };

  // Grammar check handler
  async function handleGrammarCheck() {
    if (!editorRef.current || !selectedNote) return;
    setGrammarLoading(true);
    const html = editorRef.current.getHTML();
    setGrammarOriginalHtml(html);
    try {
      const correctedHtml = await api.checkGrammar({
        html,
        noteId: selectedNote.note_id,
      });
      setGrammarCorrectedHtml(correctedHtml);
      setGrammarModalOpen(true);
    } catch (e) {
      toast.error('Grammar correction failed.');
    } finally {
      setGrammarLoading(false);
    }
  }

  // Accept grammar correction
  function handleAcceptGrammar() {
    if (editorRef.current && grammarCorrectedHtml) {
      editorRef.current.commands.setContent(grammarCorrectedHtml, false);
      setGrammarModalOpen(false);
      toast.success('Note updated with grammar corrections.');
      // Optionally, trigger save here
    }
  }

  return (
    <div className={cn(
      "flex flex-col min-h-screen",
      "bg-background text-foreground"
    )}>
      <NotesNav
        onNewNote={handleNewNote}
        onToggleRecentNotes={() => setShowRecentNotes(!showRecentNotes)}
        showRecentNotes={showRecentNotes}
      />

      <div className="mt-14">
        <div className="flex-1 relative">
          <main className="h-full overflow-y-auto">
            <div className="px-4 md:px-6 py-4">
              {/* Note Editor */}
              <div className={showRecentNotes ? "max-w-5xl pl-0" : "max-w-full"}>
                {/* Title and AI Toggle Row */}
                <div className="flex gap-8 mb-4">
                  <input
                    type="text"
                    value={selectedNote?.title || ''}
                    onChange={e => {
                      if (selectedNote) {
                        setTitleEdited(true);
                        setSelectedNoteTitle(e.target.value); // Immediate local update
                      }
                    }}
                    onBlur={async (e) => {
                      if (selectedNote) {
                        if (isDraft(selectedNote)) {
                          handleFirstEdit('title', e.target.value);
                        } else {
                          handleUpdateNote({
                            ...selectedNote,
                            title: e.target.value,
                          });
                        }
                      }
                    }}
                    placeholder="Note title"
                    className="text-2xl font-semibold bg-transparent border-none focus:outline-none w-full text-left"
                  />
                  <div className="flex items-center gap-2">
                    {/* Grammar Check Button */}
                    <button
                      type="button"
                      className="p-2 rounded-lg hover:bg-accent/50 transition"
                      title="Check Grammar"
                      onClick={handleGrammarCheck}
                      disabled={grammarLoading}
                    >
                      <SpellCheck className="w-5 h-5" />
                    </button>
                    {/* Prompt Engineer Button */}
                    <button
                      type="button"
                      className="p-2 rounded-lg hover:bg-accent/50 transition"
                      title="Prompt Engineer"
                      onClick={() => {/* TODO: Implement prompt engineer */}}
                    >
                      <Sparkles className="w-5 h-5" />
                    </button>
                    {/* Audio Mode Button */}
                    <button
                      type="button"
                      className="p-2 rounded-lg hover:bg-accent/50 transition"
                      title="Audio Mode"
                      onClick={() => {/* TODO: Implement audio mode */}}
                    >
                      <Volume2 className="w-5 h-5" />
                    </button>
                    {/* <AISwitch checked={isAIEnabled} onChange={setIsAIEnabled} /> */}
                  </div>
                </div>

                {/* Content */}
                {selectedNote && (
                  <RichTextEditor
                    value={
                      !selectedNote.content
                        ? { type: 'doc', content: [] }
                        : typeof selectedNote.content === 'string'
                          ? (() => { try { return JSON.parse(selectedNote.content); } catch { return { type: 'doc', content: [] }; } })()
                          : selectedNote.content
                    }
                    noteId={selectedNote.note_id}
                    onChange={newContent => {
                      if (selectedNote) {
                        const contentString = typeof newContent === 'string' ? newContent : JSON.stringify(newContent);
                        setSelectedNote({ ...selectedNote, content: contentString });
                      }
                    }}
                    onBlur={e => {
                      if (selectedNote) {
                        const contentString = typeof selectedNote.content === 'string' ? selectedNote.content : JSON.stringify(selectedNote.content);
                        if (isDraft(selectedNote)) {
                          handleFirstEdit('content', contentString);
                        } else {
                          handleUpdateNote({
                            ...selectedNote,
                            content: contentString,
                          });
                        }
                      }
                    }}
                    placeholder="Start writing..."
                    editorReady={handleEditorReady}
                  />
                )}
              </div>
            </div>
          </main>

          {showRecentNotes && (
            <RecentNotesPanel
              notes={notes}
              selectedNoteId={selectedNote?.note_id}
              onSelectNote={setSelectedNote}
              onClose={() => setShowRecentNotes(false)}
              onDeleteNote={handleDeleteNote}
            />
          )}
        </div>

        <FloatingNav
          onToggleTabs={() => {}}
          onToggleNotes={() => setShowRecentNotes(!showRecentNotes)}
          showTabs={false}
          showNotes={showRecentNotes}
        />
      </div>

      {/* Grammar Correction Modal */}
      <Dialog open={grammarModalOpen} onOpenChange={setGrammarModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Grammar Correction Preview</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col md:flex-row gap-6 mt-2">
            <div className="flex-1 border rounded-lg bg-muted/40 p-3 overflow-auto max-h-96">
              <div className="font-semibold mb-2 text-sm text-muted-foreground">Original</div>
              <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: grammarOriginalHtml || '' }} />
            </div>
            <div className="flex-1 border rounded-lg bg-muted/40 p-3 overflow-auto max-h-96">
              <div className="font-semibold mb-2 text-sm text-muted-foreground">Corrected</div>
              <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: grammarCorrectedHtml || '' }} />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <button
              className="px-4 py-2 rounded-md bg-primary text-white font-semibold hover:bg-primary/90 transition"
              onClick={handleAcceptGrammar}
              disabled={!grammarCorrectedHtml}
            >
              Replace in Note
            </button>
            <button
              className="px-4 py-2 rounded-md border border-input bg-background text-foreground font-semibold hover:bg-accent/30 transition"
              onClick={() => setGrammarModalOpen(false)}
            >
              Cancel
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotesPage; 